import { BrowserRouter, Routes, Route, Link, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import BookingPage from './pages/BookingPage';
import GuestInfoPage from './pages/GuestInfoPage';
import BookingCompletePage from './pages/BookingCompletePage';
import MyPage from './pages/MyPage';
import PassportPage from './pages/PassportPage';
import CheckinGuestPage from './pages/CheckinGuestPage';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import TokushohoPage from './pages/legal/TokushohoPage';
import ContactPage from './pages/legal/ContactPage';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/DashboardPage';
import AdminCalendar from './pages/admin/CalendarPage';
import AdminReservations from './pages/admin/ReservationsPage';
import AdminProperties from './pages/admin/PropertiesPage';
import AdminMessages from './pages/admin/MessagesPage';
import AdminPassport from './pages/admin/PassportPage';
import AdminCheckin from './pages/admin/CheckinPage';
import AdminCleaning from './pages/admin/CleaningPage';
import AdminSettings from './pages/admin/SettingsPage';
import AdminUsers from './pages/admin/AdminUsersPage';
// 旧URL /admin/ota-review?tab=xxx → /admin/calendar?view=ota&tab=xxx
function OtaReviewRedirect() {
  const [p] = useSearchParams();
  const tab = p.get('tab');
  return <Navigate to={`/admin/calendar?view=ota${tab ? `&tab=${encodeURIComponent(tab)}` : ''}`} replace />;
}
import CleanerApp from './pages/cleaner/CleanerApp';
import { ReactNode } from 'react';
import './styles/App.css';

// ゲスト向け共通レイアウト（ヘッダー＋フッター）
function GuestLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <>
      {/* ▼▼▼ テスト中バナー: 正式リリース時にこのブロックを削除してください ▼▼▼ */}
      <div className="test-banner" role="alert">
        {t('common.testBanner')}
      </div>
      {/* ▲▲▲ テスト中バナー ここまで ▲▲▲ */}
      <Header />
      <main className="main-content">{children}</main>
      <Footer />
    </>
  );
}

// 決済キャンセル時の戻り先
function BookingCancelPage() {
  const { t } = useTranslation();
  return (
    <div className="container" style={{ padding: '3rem 1rem', textAlign: 'center' }}>
      <h1 style={{ marginBottom: '1rem' }}>{t('common.cancel')}</h1>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
        {t('booking.selectDates')}
      </p>
      <Link
        to="/booking"
        style={{
          display: 'inline-block',
          background: '#2563eb',
          color: '#fff',
          padding: '0.75rem 2rem',
          borderRadius: '8px',
          fontWeight: 600,
        }}
      >
        {t('booking.backToDates')}
      </Link>
    </div>
  );
}

function App() {
  const { i18n } = useTranslation();

  return (
    <BrowserRouter>
      <div className="app" lang={i18n.language}>
        <Routes>
          {/* ホーム */}
          <Route
            path="/"
            element={
              <GuestLayout>
                <HomePage />
              </GuestLayout>
            }
          />

          {/* My page（予約照会） */}
          <Route
            path="/mypage"
            element={
              <GuestLayout>
                <MyPage />
              </GuestLayout>
            }
          />

          {/* 日程先・3物件横断検索 */}
          <Route
            path="/search"
            element={
              <GuestLayout>
                <SearchPage />
              </GuestLayout>
            }
          />

          {/* ゲスト予約フロー */}
          <Route
            path="/booking"
            element={
              <GuestLayout>
                <BookingPage />
              </GuestLayout>
            }
          />
          <Route
            path="/booking/guest"
            element={
              <GuestLayout>
                <GuestInfoPage />
              </GuestLayout>
            }
          />
          <Route
            path="/booking/complete"
            element={
              <GuestLayout>
                <BookingCompletePage />
              </GuestLayout>
            }
          />
          <Route
            path="/booking/cancel"
            element={
              <GuestLayout>
                <BookingCancelPage />
              </GuestLayout>
            }
          />

          {/* 利用規約・プライバシーポリシー・お問い合わせ */}
          <Route path="/terms" element={<GuestLayout><TermsPage /></GuestLayout>} />
          <Route path="/privacy" element={<GuestLayout><PrivacyPage /></GuestLayout>} />
          <Route path="/tokushoho" element={<GuestLayout><TokushohoPage /></GuestLayout>} />
          <Route path="/contact" element={<GuestLayout><ContactPage /></GuestLayout>} />

          {/* パスポート登録（ゲスト専用URL・ヘッダー/フッターなし） */}
          <Route path="/passport" element={<PassportPage />} />
          <Route path="/passport/:token" element={<PassportPage />} />

          {/* チェックイン本人確認（ゲスト向け入口。設計書どおりの /checkin。中身は PassportPage を共用） */}
          {/* 当日チェックイン業務用の受付端末画面（予約照会→顔/旅券提出→完了） */}
          <Route path="/checkin" element={<CheckinGuestPage />} />
          <Route path="/checkin/:token" element={<CheckinGuestPage />} />
          <Route path="/cleaner" element={<CleanerApp />} />

          {/* 管理画面ルート */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="reservations" element={<AdminReservations />} />
            <Route path="properties" element={<AdminProperties />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="messages/:email" element={<AdminMessages />} />
            <Route path="mail-templates" element={<Navigate to="/admin/settings?tab=mail" replace />} />
            <Route path="passport" element={<AdminPassport />} />
            <Route path="checkin" element={<AdminCheckin />} />
            <Route path="cleaning" element={<AdminCleaning />} />
            <Route path="checkin-device" element={<Navigate to="/admin/settings?tab=device" replace />} />
            <Route path="settings" element={<AdminSettings />} />
          <Route path="admin-users" element={<AdminUsers />} />
            <Route path="ota-review" element={<OtaReviewRedirect />} />
          </Route>
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
